import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type {
  VerdaAvailabilityList,
  VerdaBalance,
  VerdaInstanceTypeList,
  VerdaLocationList,
  VerdaProductLinkList,
  VerdaVolumeProductCreateResponse,
  VerdaVolumeProductLinkList,
} from '../types/models/Verda';
import type {
  ApiResponseVerdaAvailability,
  ApiResponseVerdaBalance,
  ApiResponseVerdaInstanceTypes,
  ApiResponseVerdaLocations,
  ApiResponseVerdaProductLinks,
  ApiResponseVerdaVolumeProductCreate,
  ApiResponseVerdaVolumeProducts,
} from '../types/api/ApiResponseVerda';

// Staff-only hooks over docs/api-types/admin-verda.md. Mount them behind the `is_ancalagon_staff`
// gate (the Verda page self-guards with <NotFound />).
//
// Every read is live from Verda, which rate-limits at 60 req/min per endpoint, so each hook fetches
// **once** on mount and then only through `refetch` (the tab's Refresh button). They deliberately do
// not re-run when `apiFetch`'s identity changes on an OIDC silent renew (that would amount to
// polling); the guard also absorbs StrictMode's double effect invocation.

interface VerdaReadState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetch `path` once (when `enabled` and authenticated), keeping `pick(body)` of a JSend success.
 * `pick` returns `null` for a success whose payload is missing its expected keys, which is then
 * reported as an error. Pass a module-level `pick` so its identity is stable.
 */
function useVerdaFetchOnce<R extends { status: string; message?: string }, T>(
  path: string,
  pick: (body: R) => T | null,
  what: string,
  enabled: boolean,
): VerdaReadState<T> {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const hasFetchedRef = useRef(false);

  const fetchData = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(path, { signal: controller.signal });
      const body = (await response.json().catch(() => null)) as R | null;
      const picked = body?.status === 'success' ? pick(body) : null;
      if (picked !== null) {
        setData(picked);
        return;
      }
      setError(body?.message ?? `Could not load the Verda ${what} (HTTP ${response.status}).`);
    } catch (err) {
      if ((err instanceof DOMException && err.name === 'AbortError') || controller.signal.aborted) {
        return;
      }
      setError(err instanceof Error ? err.message : `An error occurred while loading the Verda ${what}.`);
    } finally {
      if (abortRef.current === controller) {
        setLoading(false);
      }
    }
  }, [apiFetch, path, pick, what]);

  useEffect(() => {
    if (enabled && auth.isAuthenticated && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      void fetchData();
    }
  }, [enabled, auth.isAuthenticated, fetchData]);

  return { data, loading, error, refetch: fetchData };
}

const pickBalance = (body: ApiResponseVerdaBalance): VerdaBalance | null => body.data?.balance ?? null;

const pickLocations = (body: ApiResponseVerdaLocations): VerdaLocationList | null =>
  body.data?.locations && body.data.fetched_at
    ? { locations: body.data.locations, fetched_at: body.data.fetched_at }
    : null;

const pickInstanceTypes = (body: ApiResponseVerdaInstanceTypes): VerdaInstanceTypeList | null =>
  body.data?.instance_types && body.data.currency && body.data.fetched_at
    ? { instance_types: body.data.instance_types, currency: body.data.currency, fetched_at: body.data.fetched_at }
    : null;

const pickAvailability = (body: ApiResponseVerdaAvailability): VerdaAvailabilityList | null =>
  body.data?.availability && body.data.fetched_at
    ? { availability: body.data.availability, fetched_at: body.data.fetched_at }
    : null;

const pickProductLinks = (body: ApiResponseVerdaProductLinks): VerdaProductLinkList | null =>
  body.data?.products ? { products: body.data.products } : null;

const pickVolumeProducts = (body: ApiResponseVerdaVolumeProducts): VerdaVolumeProductLinkList | null =>
  Array.isArray(body.data?.products) ? { products: body.data.products } : null;

/** `GET /admin/verda/balance` — the Verda account balance. */
export const useAdminVerdaBalance = (enabled: boolean = true) => {
  const { data, ...rest } = useVerdaFetchOnce('/admin/verda/balance', pickBalance, 'balance', enabled);
  return { balance: data, ...rest };
};

/** `GET /admin/verda/locations` — every Verda datacenter location, in Verda's order. */
export const useAdminVerdaLocations = (enabled: boolean = true) => {
  const { data, ...rest } = useVerdaFetchOnce('/admin/verda/locations', pickLocations, 'locations', enabled);
  return { locations: data?.locations ?? null, fetchedAt: data?.fetched_at ?? null, ...rest };
};

/**
 * `GET /admin/verda/instance-types` — every instance type with its per-hour prices, in Verda's order.
 * Uses the backend's default currency (EUR, the account's); `?currency=usd` is not exposed in the UI.
 */
export const useAdminVerdaInstanceTypes = (enabled: boolean = true) => {
  const { data, ...rest } = useVerdaFetchOnce(
    '/admin/verda/instance-types',
    pickInstanceTypes,
    'instance types',
    enabled,
  );
  return {
    instanceTypes: data?.instance_types ?? null,
    currency: data?.currency ?? null,
    fetchedAt: data?.fetched_at ?? null,
    ...rest,
  };
};

/**
 * `GET /admin/verda/instance-availability` — a live capacity snapshot: per location, the instance-type
 * codes deployable on demand and as spot. The backend makes two Verda calls per request (on-demand +
 * spot) from one shared Verda bucket, so this route allows only 30 calls/min — never poll it.
 */
export const useAdminVerdaAvailability = (enabled: boolean = true) => {
  const { data, ...rest } = useVerdaFetchOnce(
    '/admin/verda/instance-availability',
    pickAvailability,
    'availability',
    enabled,
  );
  return { availability: data?.availability ?? null, fetchedAt: data?.fetched_at ?? null, ...rest };
};

/**
 * `GET /admin/verda/products` — the catalog products created so far from Verda instance types, one link
 * per linked type. A Manager-side read (no Verda call); refetched after a create.
 */
export const useAdminVerdaProductLinks = (enabled: boolean = true) => {
  const { data, ...rest } = useVerdaFetchOnce('/admin/verda/products', pickProductLinks, 'product links', enabled);
  return { links: data?.products ?? null, ...rest };
};

/**
 * `GET /admin/verda/volume-products` — the storage products created so far from Verda volume types (they
 * price supplementary storage, and the NVMe one the fixed server disk, in customer GPU budgets). A
 * Manager-side read (no Verda call); refetched after a create.
 */
export const useAdminVerdaVolumeProducts = (enabled: boolean = true) => {
  const { data, ...rest } = useVerdaFetchOnce(
    '/admin/verda/volume-products',
    pickVolumeProducts,
    'storage products',
    enabled,
  );
  return { products: data?.products ?? null, ...rest };
};

export type VerdaVolumeProductCreateOutcome =
  /** The run happened: per-type failures are `failed` rows inside it (the other types were still processed). */
  | { status: 'success'; response: VerdaVolumeProductCreateResponse }
  /** A whole-call refusal (missing category, a concurrent run, Verda not configured / rejected), verbatim. */
  | { status: 'error'; message: string };

/**
 * `POST /admin/verda/volume-products` — create the missing storage products, with NO body (the backend
 * refuses any other body). Create-only and idempotent: a linked type comes back `exists`, untouched.
 * It reads Verda's live price once per missing type, so it is only ever run on an explicit staff click.
 * Never throws.
 */
export const useAdminVerdaCreateVolumeProducts = () => {
  const apiFetch = useApiFetch();
  const [loading, setLoading] = useState(false);

  const create = useCallback(async (): Promise<VerdaVolumeProductCreateOutcome> => {
    setLoading(true);
    try {
      const response = await apiFetch('/admin/verda/volume-products', { method: 'POST' });
      // Branch on the JSend body.status, NEVER response.ok (errors arrive at HTTP 200).
      const json = (await response.json().catch(() => null)) as ApiResponseVerdaVolumeProductCreate | null;
      const data = json?.data;
      if (
        json?.status === 'success' &&
        Array.isArray(data?.results) &&
        data?.summary &&
        typeof data.currency === 'string' &&
        typeof data.markup_percent === 'string'
      ) {
        return {
          status: 'success',
          response: {
            results: data.results,
            summary: data.summary,
            currency: data.currency,
            markup_percent: data.markup_percent,
          },
        };
      }
      return {
        status: 'error',
        message: json?.message ?? `Could not create the storage products (HTTP ${response.status}).`,
      };
    } catch (err) {
      return { status: 'error', message: err instanceof Error ? err.message : 'An unexpected error occurred.' };
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  return { create, loading };
};

export type VerdaLocationsState = ReturnType<typeof useAdminVerdaLocations>;
export type VerdaProductLinksState = ReturnType<typeof useAdminVerdaProductLinks>;
export type VerdaInstanceTypesState = ReturnType<typeof useAdminVerdaInstanceTypes>;
